// Bundle-update pill. Shows when the build-version watcher detects a
// server-side bundle bump. Mounted under <App />, so it appears on
// every surface (login, kid PIN picker, kid app, adult shell) the
// moment the mismatch event fires.
//
// Distinct from the existing <UpdateBanner /> — that one is the
// Android-APK update flow (versionName + download + install). This is
// the web-tab bundle reload prompt for "the server has a newer JS
// bundle than the one running in your tab."
//
// Behavior:
//   • Renders a small soft pill anchored to the top center of the
//     viewport.
//   • "Refresh now" button reloads immediately.
//   • Dismiss (×) hides until next page load. The watcher's deferred
//     auto-reload still fires on visibility-hidden or 10 min idle, so
//     dismissal is purely a UX preference, not an opt-out.

import { useEffect, useState } from "preact/hooks";
import { UPDATE_AVAILABLE_EVENT, refreshNow } from "../lib/build-version";
import { useUpdateCheck } from "../lib/use-update-check";

export function BundleUpdatePill() {
  const [open, setOpen] = useState(false);
  const apkUpdate = useUpdateCheck();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(UPDATE_AVAILABLE_EVENT, handler as EventListener);
    return () => window.removeEventListener(UPDATE_AVAILABLE_EVENT, handler as EventListener);
  }, []);

  // On Android, when an APK update is also pending, suppress this pill —
  // installing the APK refreshes the bundle too, so stacking both
  // notifications just looks like a double-fire bug. UpdateBanner covers
  // the case; here we'd be redundant. Plain browsers (surface "browser")
  // still get the pill because they have no APK path.
  if (apkUpdate.available && apkUpdate.surface !== "browser") return null;
  if (!open) return null;

  return (
    <div className="bundle-update-pill" role="status" aria-live="polite">
      <span className="bundle-update-pill-emoji" aria-hidden>✨</span>
      <span className="bundle-update-pill-text">New version available</span>
      <button
        type="button"
        className="bundle-update-pill-action"
        onClick={() => refreshNow()}
      >
        Refresh
      </button>
      <button
        type="button"
        className="bundle-update-pill-dismiss"
        onClick={() => setOpen(false)}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
